import {create} from '@bufbuild/protobuf';
import {
  PartnerKioskUpdate_UserSessionUpdateData, PartnerKioskUpdate_UserSessionUpdateDataSchema,
  PartnerKioskUpdate_UserSessionUpdateData_UserSessionState, SessionPlayMode as LegacySessionPlayMode,
  BetOutcome as LegacyBetOutcome
} from '../../generated/partner_service_pb';
import {PlayerState, UserSessionState as BayUserSessionState} from '../../generated/bay_session_shared_pb';

function toPartnerUserSessionState(state: BayUserSessionState): PartnerKioskUpdate_UserSessionUpdateData_UserSessionState {
  switch (state) {
    case BayUserSessionState.NOT_JOINED:
      return PartnerKioskUpdate_UserSessionUpdateData_UserSessionState.NOT_JOINED;
    case BayUserSessionState.NOT_ACTIVE:
      return PartnerKioskUpdate_UserSessionUpdateData_UserSessionState.NOT_ACTIVE;
    case BayUserSessionState.ACTIVE:
      return PartnerKioskUpdate_UserSessionUpdateData_UserSessionState.ACTIVE;
    default:
      return PartnerKioskUpdate_UserSessionUpdateData_UserSessionState.UNKNOWN;
  }
}

// PlayerState (per-user channel from bay_session.streamPlayerState) does not
// carry the cross-player fields that the legacy partner-service
// PartnerKioskUpdate_UserSessionUpdateData payload exposed. Specifically
// absent from PlayerState:
//   - roundRobinState (player order, usernames, shotsTaken/Remaining)
//   - sessionCurrentActivePlayerUsername
//   - isSessionFull
//   - sessionId
//
// Those four fields live on the per-station StreamSessionState channel, which
// the mobile app is not yet subscribed to (separate BSA-4-x follow-up). Until
// that subscription lands, the synthetic UserSessionUpdateData this adapter
// produces leaves those fields at their proto3 defaults. Consumers of
// roundRobinStatus continue to read the Context default (null) that
// StoreContextProvider seeds — the rotation UI idles until the station
// subscription is wired up.
//
// accountSecurityBypassEnabled IS derived from PlayerState's own
// SessionPlayer.confirmation surface — specifically from on_SHOT (NOT
// on_turn). ConfirmationSettings ships on the per-user channel (every
// player gets their own settings), so the bay-session-native flow does
// not need to wait on the per-station stream for this signal. Mapping
// mirrors the server's projection (session_logic_helpers.go
// deriveAccountSecurityBypass): on_shot=false ↔ bypass=true ("skip the
// accept dialog before each shot" / autoplay); on_shot=true or unset ↔
// bypass=false (require the accept dialog — venue default = ask).
// SMG-3048: previously this was incorrectly derived from on_turn, which
// broke confirm-on-shot — on_turn defaults to false on join, so the
// buggy mapping pinned bypass=true and the per-shot accept dialog never
// appeared, with the bet/challenge silently never created.
//
// The fields below DO map cleanly: SessionPlayer overlaps with the participant-
// preview fields the dispatcher reads (target / previewed / previous distances,
// has_accepted_bet, previous_target_outcome). PlayerState.userSessionState is
// already the server-derived membership classifier; this adapter only
// translates between the bay-session and legacy partner-service enum shapes.
export function playerStateToUserSessionData(state: PlayerState | undefined): PartnerKioskUpdate_UserSessionUpdateData | undefined {
  if (!state) return undefined;

  const player = state.sessionPlayer;
  const userSessionState = toPartnerUserSessionState(state.userSessionState);

  // SessionPlayMode and BetOutcome are independent enums across the two proto
  // packages but share numeric values for the overlap we care about
  // (UNKNOWN/UNSPECIFIED=0, SOLO=1, OPEN=2, ROTATION=3 for SessionPlayMode;
  // UNSPECIFIED=0, WON=1, LOST=2, CANCELLED=3, EXPIRED=4 for BetOutcome). The
  // numeric cast preserves that mapping; new bay_session-only values (FRS=4,
  // EVENT=5 on SessionPlayMode) collapse to UNKNOWN in the legacy enum which
  // is the safe default for downstream UI gates.
  // SMG-3048: derive the per-SHOT accept-dialog bypass from on_shot (NOT
  // on_turn — that's the per-TURN confirmation, a separate signal). Mirrors
  // the server's projection (session_logic_helpers.go
  // deriveAccountSecurityBypass): on_shot=false -> bypass=true ("skip the
  // accept dialog before each shot" / autoplay); on_shot=true or unset ->
  // bypass=false (require the accept dialog, venue default = ask).
  const onShot = player?.confirmation?.onShot;
  const accountSecurityBypassEnabled = onShot === false;

  return create(PartnerKioskUpdate_UserSessionUpdateDataSchema, {
    sessionId: '',
    userSessionState,
    // SMG-3061: PlayerState now carries the station's active player's first
    // name on its own per-user projection (server-side fill in
    // buildPlayerStateSnapshot). Surface it on the partner-shape
    // sessionCurrentActivePlayerUsername field so the existing
    // FullSwingHomePage "{name} is currently playing" copy lights up in
    // BSA-native mode (legacy partner-stream is the other source). Empty
    // string when there's no active player (SOLO/OPEN with nobody
    // designated, or rotation gap between turns).
    sessionCurrentActivePlayerUsername: state.activePlayerFirstName ?? '',
    isSessionFull: false,
    accountSecurityBypassEnabled,
    targetDistanceYards: player?.targetDistanceYards ?? 0n,
    previewedTargetDistanceYards: player?.previewedTargetDistanceYards ?? 0n,
    previewedNextTargetYards: player?.previewedNextTargetYards ?? 0n,
    previousTargetDistanceYards: player?.previousTargetDistanceYards ?? 0n,
    previousTargetOutcome: (player?.previousTargetOutcome ?? 0) as unknown as LegacyBetOutcome,
    hasAcceptedBet: player?.hasAcceptedBet ?? false,
    playMode: state.stationPlayMode as unknown as LegacySessionPlayMode,
  });
}
